/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { useDetectWindowSize, useLocalize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Grid,
  Icon,
  InlineMessage,
  Link as DSLink,
  Loader,
  ModalIsland,
  RadioButton,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { CredentialRequestAccountSelection } from '../../../../models/PamModels';
import { msgs } from '../../../../utilities/messages';
import {
  ACCOUNT_JIT_SELECTION,
  MAX_ACCOUNTS_BEFORE_SPLICE,
  MAX_ACCOUNTS_TO_SHOW,
  MODAL_PAGE_KINDS,
} from '../../constants';
import {
  getAcctDescTooltipCustomMaxWidth,
  isAcctDescToolTipDisabled,
  isAcctDescWithinToolTipLimit,
  PIXELS_PER_REM,
} from '../../helpers';

import './MpaAccountSelection.css';

function MpaAccountSelection({
  modalPageKind,
  isAssetTypeDatabase,
  isAssetTypeServer,
  isFetching,
  isError,
  accountListArray,
  accountSelection,
  setAccountSelection,
  setIsAccountListModal,
  databaseJitAccountInfo,
  setDatabaseJitAccountInfo,
  setIsDatabaseJitCreateAccount,
  accountsTotalCount,
}) {
  const pamJitAcctFlag = window.localStorage.getItem('pamJitAcct');
  const isPamJitAcct = parseInt(pamJitAcctFlag, 10);

  const localize = useLocalize();
  const { Item } = Grid;
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(isError);
  const [selectedAccountWarningModal, setSelectedAccountWarningModal] =
    useState(null);

  const VIEWPORT_WIDTH = useDetectWindowSize().width / PIXELS_PER_REM;

  const isCTS = modalPageKind === MODAL_PAGE_KINDS.CTS;

  const accountSelectionClasses = classnames(
    'MpaAccountSelection',
    `MpaAccountSelection-${modalPageKind}`
  );

  const MORE_LINK_TEXT = localize(msgs.pam.modalPage.accountSelection.moreLink);
  const DB_LABEL_HEADING_TEXT = localize(
    msgs.pam.modalPage.accountSelection.dataBaseLabelHeading
  );
  const DB_LABEL_SUBHEADING_TEXT = localize(
    msgs.pam.modalPage.accountSelection.dataBaseLabelSubheading
  );
  const DB_JIT_BUTTON_TEXT = localize(
    msgs.pam.modalPage.accountSelection.dataBaseButton
  );
  const JIT_RADIO_LABEL_TEXT = localize(
    msgs.pam.modalPage.accountSelection.jitRadioLabel
  );
  const ACCOUNT_RADIO_GROUP_LABEL_TEXT = localize(
    msgs.pam.modalPage.accountSelection.accountRadioGroupLabel
  );
  const DB_JIT_TEMP_PRIV_ACCOUNT_TEXT = localize(
    msgs.pam.cts.db.jitTempPrivilegedAccount
  );
  const DB_JIT_ENTITLEMENTS_TEXT = localize(msgs.pam.cts.db.jitEntitlements);
  const NO_ACCOUNTS_TEXT = localize(
    msgs.pam.modalPage.accordionHeading.noAccountRecords
  );
  const EDIT_TEXT = localize(msgs.pam.common.buttons.edit);
  const ACCT_DESC_TRUNCATE_TEXT = localize(
    msgs.pam.modalPage.accountSelection.accountDescriptionTruncateLabel
  );

  const CHANGE_ACCOUNT_TEXT = localize(
    msgs.pam.modalPage.accountSelection.changeAccount
  );
  const CANCEL_TEXT = localize(msgs.pam.common.cancel);

  const INLINE_MSG = localize(
    msgs.pam.modalPage.accountSelection.inLineMessage
  );
  const ALERT_TITLE = localize(msgs.pam.modalPage.accountSelection.alertTitle);
  const ALERT_SUB_TITLE = localize(
    msgs.pam.modalPage.accountSelection.alertSubTitle
  );
  const JIT_RADIO_TITLE = localize(msgs.pam.cts.db.jitTempPrivAcctModalTitle);
  const JIT_RADIO_DESC = localize(msgs.pam.cts.db.jitTempAcctModalSubTitle);

  const isDatabaseJitAccountCreated =
    databaseJitAccountInfo?.entitlements?.length > 0;

  useEffect(() => {
    setIsErrorMessageVisible(isError);
  }, [isError]);

  const handleWarningModalClick = () => {
    if (selectedAccountWarningModal === ACCOUNT_JIT_SELECTION.value) {
      setAccountSelection(ACCOUNT_JIT_SELECTION);
    } else {
      setAccountSelection(selectedAccountWarningModal);
    }

    setDatabaseJitAccountInfo({ entitlements: [] });
    setIsWarningModalOpen(false);
  };

  const handleClick = (e, value) => {
    e.stopPropagation();
    setSelectedAccountWarningModal(value);

    if (isDatabaseJitAccountCreated) {
      // show the warning modal
      setIsWarningModalOpen(true);
    } else if (value === ACCOUNT_JIT_SELECTION.value) {
      setAccountSelection(ACCOUNT_JIT_SELECTION);
    } else {
      setAccountSelection(value);
    }
  };

  const newAccountListArray = Array.isArray(accountListArray)
    ? [...accountListArray]
    : [];

  // find the selected account object to pass to RadioButton
  const selectedAccountObj = accountListArray?.find(
    (account) => account.value === accountSelection?.value
  );

  if (newAccountListArray.length > 0) {
    const selectedIndex = newAccountListArray.findIndex(
      (account) => account.value === accountSelection?.value
    );

    if (selectedIndex > MAX_ACCOUNTS_BEFORE_SPLICE) {
      const [selectedItem] = newAccountListArray.splice(selectedIndex, 1);

      newAccountListArray.unshift(selectedItem);
    }
  }

  // Add new asset types that support JIT here to apply renderJITByAssetType functional components
  const isCTSWithJITSupport = Boolean(
    isCTS && (isAssetTypeServer || isAssetTypeDatabase) && isPamJitAcct
  );

  const renderJITByAssetType = () => {
    switch (true) {
      case isAssetTypeDatabase:
        return (
          <Box className='MpaAccountSelection-createNewAccount'>
            <Box className='MpaAccountSelection-jitDatabaseLabelGroup'>
              <Typography kind='h3'>{DB_LABEL_HEADING_TEXT}</Typography>
              <Typography
                kind='body2'
                className='MpaAccountSelection-subheading'>
                {DB_LABEL_SUBHEADING_TEXT}
              </Typography>
            </Box>
            {isDatabaseJitAccountCreated ? (
              <Box
                tag='section'
                className='MpaAccountSelection-jitDatabaseAccountWrapper'>
                <div className='MpaAccountSelection-jitDatabaseAccountInfo'>
                  <Typography kind='h3'>
                    {DB_JIT_TEMP_PRIV_ACCOUNT_TEXT}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='MpaAccountSelection-entitlementsRow'>
                    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                    <span>{DB_JIT_ENTITLEMENTS_TEXT}:</span>
                    <span className='MpaAccountSelection-entitlements'>
                      {databaseJitAccountInfo.entitlements.join(', ')}
                    </span>
                  </Typography>
                </div>
                <ButtonIcon
                  size='large'
                  kind='outlinedSecondary'
                  icon={<Icon kind='edit' color='neutral-100' size='medium' />}
                  onClick={() => {
                    setIsDatabaseJitCreateAccount(true);
                  }}>
                  {EDIT_TEXT}
                </ButtonIcon>
              </Box>
            ) : (
              <Button
                className='MpaAccountSelection-jitButton'
                type='button'
                kind='filled'
                size='large'
                onClick={() => {
                  setIsDatabaseJitCreateAccount(true);
                }}>
                {DB_JIT_BUTTON_TEXT}
              </Button>
            )}
          </Box>
        );

      case isAssetTypeServer:
        return (
          <Box className='MpaAccountSelection-createNewAccount'>
            <Typography kind='h3'>{JIT_RADIO_LABEL_TEXT}</Typography>
            <Grid spacing={2} className='MpaAccountSelection-radioButtonGroup'>
              <Item
                desktop={6}
                tablet={12}
                className='MpaAccountSelection-radioButton'>
                <RadioButton
                  id='accountRadio-jit'
                  name='accountSelectionRadioGroup'
                  title={JIT_RADIO_TITLE}
                  description={JIT_RADIO_DESC}
                  value={ACCOUNT_JIT_SELECTION.value}
                  radioSelection={accountSelection?.value}
                  onClick={handleClick}
                  titleLineLimit={2}
                  descriptionLineLimit={2}
                  className='MpaAccountSelection-radioButtonFullWidth'
                />
              </Item>
            </Grid>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box tag='section' className={accountSelectionClasses}>
      {isCTSWithJITSupport && renderJITByAssetType()}
      <Box className='MpaAccountSelection-selectCurrentAccount'>
        {isCTSWithJITSupport && (
          <Typography kind='h3'>{ACCOUNT_RADIO_GROUP_LABEL_TEXT}</Typography>
        )}
        {isFetching && (
          <Box tag='div' className='SpfEndpoint-loader'>
            <Loader kind='dots' format='inline' color='info' size='medium' />
          </Box>
        )}
        {isError && (
          <InlineMessage
            className='MpaAccountSelection-InlineMsg'
            kind='background'
            text={INLINE_MSG}
            colorTheme='critical'
            leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
            isVisible={isErrorMessageVisible}
            onDismiss={() => setIsErrorMessageVisible(false)}
            isDismissible
            size='small'
          />
        )}
        {(newAccountListArray?.length === 0 || !newAccountListArray) &&
        !isFetching &&
        !isError ? (
          <InlineMessage
            classNames={{
              rootClassName: 'MpaAccountSelection-noRecords',
            }}>
            <Box className='MpaAccountSelection-noRecords'>
              <Icon kind='AlertCriticalOutline' color='navy-900' />
              <Typography
                kind='h3'
                className='MpaAccountSelection-noRecords-title'>
                {NO_ACCOUNTS_TEXT}
              </Typography>
            </Box>
          </InlineMessage>
        ) : (
          <Grid spacing={2} className='MpaAccountSelection-radioButtonGroup'>
            {newAccountListArray
              ?.slice(0, MAX_ACCOUNTS_TO_SHOW)
              .map((account, index) => (
                <Item
                  key={`accountRadio-${account.value}`}
                  desktop={6}
                  tablet={12}
                  className='MpaAccountSelection-radioButton'>
                  <Tooltip
                    title={ACCT_DESC_TRUNCATE_TEXT}
                    aria-label={ACCT_DESC_TRUNCATE_TEXT}
                    text={account.description}
                    isDisabled={isAcctDescToolTipDisabled(account.description)}
                    maxWidth={
                      isAcctDescWithinToolTipLimit(account.description)
                        ? '11rem'
                        : getAcctDescTooltipCustomMaxWidth(VIEWPORT_WIDTH)
                    }
                    trigger={
                      <RadioButton
                        className='MpaAccountSelection-radioButtonFullWidth'
                        id={`accountRadio-${index}`}
                        name='accountSelectionRadioGroup'
                        title={account.title}
                        description={account.description}
                        value={account}
                        radioSelection={selectedAccountObj}
                        onClick={handleClick}
                        titleLineLimit={2}
                        descriptionLineLimit={2}
                      />
                    }
                    isPortalEnabled
                  />
                </Item>
              ))}
          </Grid>
        )}
        {accountsTotalCount > MAX_ACCOUNTS_TO_SHOW ? (
          <DSLink
            kind='button'
            text={MORE_LINK_TEXT}
            size='large'
            onClick={() => setIsAccountListModal(true)}
            className='MpaAccountSelection-moreLink'
          />
        ) : null}
      </Box>
      {isDatabaseJitAccountCreated && (
        <ModalIsland
          kind='warning'
          alertTitle={ALERT_TITLE}
          alertSubtitle={ALERT_SUB_TITLE}
          onClose={() => setIsWarningModalOpen(false)}
          isOpen={isWarningModalOpen}
          primaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              onClick={handleWarningModalClick}>
              {CHANGE_ACCOUNT_TEXT}
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={() => setIsWarningModalOpen(false)}>
              {CANCEL_TEXT}
            </Button>
          }
        />
      )}
    </Box>
  );
}

MpaAccountSelection.propTypes = {
  modalPageKind: PropTypes.string.isRequired,
  isAssetTypeDatabase: PropTypes.bool.isRequired,
  isAssetTypeServer: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  accountListArray: PropTypes.arrayOf(CredentialRequestAccountSelection),
  accountSelection: CredentialRequestAccountSelection,
  setAccountSelection: PropTypes.func.isRequired,
  setIsAccountListModal: PropTypes.func.isRequired,
  databaseJitAccountInfo: PropTypes.shape({
    entitlements: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  setDatabaseJitAccountInfo: PropTypes.func.isRequired,
  setIsDatabaseJitCreateAccount: PropTypes.func.isRequired,
  accountsTotalCount: PropTypes.number.isRequired,
};

MpaAccountSelection.defaultProps = {
  accountListArray: null,
  accountSelection: null,
};

export default MpaAccountSelection;
